import { createContext, use, useCallback, useMemo, useState, type ReactNode } from "react";
import { CheckCircle2, ChevronLeft, ChevronRight, CircleAlert, Info } from "lucide-react";
import { Button } from "./Button";
import { HeaderPortal, useHasHeaderSlot } from "@/layout/PageChrome";

/* ── Page header ──────────────────────────────────────────── */

export function PageHeader({
  title,
  description,
  action,
  divider = true,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  /**
   * Kept for the rare page that renders its own heading in place rather than
   * in the shell header. Ignored when the heading is portalled, which is the
   * normal case — there is nothing below it to divide.
   */
  divider?: boolean;
}) {
  const content = (
    <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1">
      <div className="min-w-0">
        <h1 className="truncate font-title text-lg leading-tight font-semibold tracking-[-0.015em] text-ink">
          {title}
        </h1>
        {description ? <p className="mt-0.5 truncate text-[13px] text-muted">{description}</p> : null}
      </div>
      {action ? <div className="flex shrink-0 flex-wrap items-center gap-3">{action}</div> : null}
    </div>
  );

  /*
   * The heading belongs in the app header, not at the top of the page body.
   * Both used to exist and both named the same thing, costing ~79px of a
   * viewport that D-15 says will never scroll. `HeaderPortal` renders nothing
   * when there is no shell (the karigar app, the 404 page), so those keep a
   * normal in-page heading.
   */
  if (useHasHeaderSlot()) {
    return <HeaderPortal>{content}</HeaderPortal>;
  }

  return (
    <header
      className={[
        "flex shrink-0 flex-wrap items-start justify-between gap-3",
        divider ? "mb-4 border-b border-rule pb-3" : "",
      ].join(" ")}
    >
      {content}
    </header>
  );
}

/* ── Empty state ──────────────────────────────────────────── */

export function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
      <EmptyMark />
      <p className="mt-3 font-title text-base font-semibold text-ink">{title}</p>
      <p className="max-w-[46ch] text-sm leading-relaxed text-muted">{message}</p>
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

/**
 * A QR finder pattern with its sibling modules, half-drawn: "nothing scanned
 * here yet", in the product's own visual language.
 */
function EmptyMark() {
  return (
    <span
      aria-hidden="true"
      className="relative grid size-14 place-items-center rounded-lg bg-brand-light ring-1 ring-brand-border ring-inset"
    >
      <span className="grid size-7 place-items-center rounded-[5px] border-[3px] border-brand/70">
        <span className="size-2.5 rounded-[2px] bg-brand/70" />
      </span>
      <span className="absolute top-2 right-2 size-1.5 rounded-[1px] bg-brand/40" />
      <span className="absolute right-2 bottom-2 size-1.5 rounded-[1px] bg-brand/25" />
      <span className="absolute bottom-2 left-2 size-1.5 rounded-[1px] bg-brand/40" />
    </span>
  );
}

/* ── Skeleton ─────────────────────────────────────────────── */

export function Skeleton({ className = "h-4 w-full" }: { className?: string }) {
  return <div className={`gq-skeleton rounded-sm ${className}`} />;
}

export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="flex flex-col gap-4 px-5 py-5">
      {Array.from({ length: rows }, (_, r) => (
        <div className="flex gap-4" key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton className="h-4 flex-1" key={c} />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ── Pagination ───────────────────────────────────────────── */

export function Pagination({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <nav
      aria-label="Pagination"
      className="flex shrink-0 items-center justify-between gap-4 border-t border-rule/70 px-5 py-2.5"
    >
      <p className="text-[13px] text-muted">
        Page <span className="font-medium tabular-nums text-ink">{page}</span> of{" "}
        <span className="tabular-nums">{totalPages}</span>
        <span className="mx-1.5 text-rule">·</span>
        <span className="tabular-nums">{total}</span> total
      </p>
      <div className="flex gap-1.5">
        <Button
          aria-label="Previous page"
          disabled={page <= 1}
          icon={<ChevronLeft aria-hidden="true" className="size-4" />}
          onClick={() => onChange(page - 1)}
          size="sm"
          variant="secondary"
        >
          <span className="hidden sm:inline">Previous</span>
        </Button>
        <Button
          aria-label="Next page"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
          size="sm"
          variant="secondary"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight aria-hidden="true" className="size-4" />
        </Button>
      </div>
    </nav>
  );
}

/* ── Tabs ─────────────────────────────────────────────────── */

export function Tabs({
  value,
  onChange,
  items,
}: {
  value: string;
  onChange: (value: string) => void;
  items: ReadonlyArray<{ value: string; label: string; count?: number }>;
}) {
  return (
    // A filter, not a section: a segmented control, visually distinct from the
    // underlined section tabs (LinkTabs) that own a URL.
    <div
      className="inline-flex max-w-full flex-wrap self-start gap-0.5 rounded-md bg-ink/[0.05] p-0.5"
      role="tablist"
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            aria-selected={active}
            className={[
              "flex items-center gap-1.5 rounded-[calc(var(--gq-radius-md)-2px)] px-3 py-1.5 font-title text-[13px] font-medium whitespace-nowrap transition-all",
              active
                ? "bg-raised text-ink shadow-[0_1px_2px_rgb(0_0_0/0.08),0_0_0_1px_rgb(0_0_0/0.04)]"
                : "text-muted hover:text-ink",
            ].join(" ")}
            key={item.value}
            onClick={() => onChange(item.value)}
            role="tab"
            type="button"
          >
            {item.label}
            {item.count !== undefined ? (
              <span
                className={`rounded-full px-1.5 text-[11px] leading-[1.125rem] tabular-nums ${
                  active ? "bg-brand-light text-brand-ink" : "bg-ink/[0.06] text-muted"
                }`}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/* ── Toast ────────────────────────────────────────────────── */

interface Toast {
  id: number;
  kind: "success" | "danger" | "info";
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi>({
  success: () => undefined,
  error: () => undefined,
  info: () => undefined,
});

export function useToast(): ToastApi {
  return use(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((kind: Toast["kind"], message: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push("success", m),
      error: (m) => push("danger", m),
      info: (m) => push("info", m),
    }),
    [push],
  );

  // Dark, whatever the tenant's surface — a toast has to read against any page.
  const icon: Record<Toast["kind"], ReactNode> = {
    success: <CheckCircle2 aria-hidden="true" className="size-4 shrink-0 text-[#6EE7A1]" />,
    danger: <CircleAlert aria-hidden="true" className="size-4 shrink-0 text-[#FF9B92]" />,
    info: <Info aria-hidden="true" className="size-4 shrink-0 text-white/70" />,
  };

  return (
    <ToastContext value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4"
      >
        {toasts.map((t) => (
          <div
            className="pointer-events-auto flex max-w-md animate-pop items-center gap-2.5 rounded-lg bg-[#17130F] py-2.5 pr-4 pl-3.5 text-sm text-white shadow-pop ring-1 ring-white/10"
            key={t.id}
            role="status"
          >
            {icon[t.kind]}
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext>
  );
}
